import { ArrowRight, Server, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";


import { toast } from "@/components/ui/use-toast";
import ParticleNetwork from "./ParticleNetwork";
import { motion } from "framer-motion";

const HeroSection = () => {
  const { t } = useLanguage();

  const targetSegments = [
    t("hero_segment_public"),
    t("hero_segment_manufacturing"),
    t("hero_segment_compliance"),
    t("hero_segment_m365"),
    t("hero_segment_rag"),
  ];


  const handleDeployAIClick = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    toast({
      title: t("ai_deployment_info"),
      description: t("ai_deployment_desc"),
    });
  };

  const handleExploreAutomationClick = () => {
    // Scroll to automation section within solutions
    document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" });
    toast({
      title: t("enterprise_automation_toast"),
      description: t("enterprise_automation_desc"),
    });
  };



  const features = [
    {
      icon: <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />,
      title: t("private_secure"),
      description: t("private_secure_desc")
    },
    {
      icon: <Zap className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
      title: t("powerful_accessible"),
      description: t("powerful_accessible_desc")
    },
    {
      icon: <Server className="w-6 h-6 text-blue-500 dark:text-blue-400" />,
      title: t("integrated_automated"),
      description: t("integrated_automated_desc")
    }
  ];

  return (
    <section id="home" className="relative flex min-h-screen items-center justify-center overflow-hidden py-24 sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100/70 to-blue-50/70 dark:from-slate-950 dark:via-slate-900/70 dark:to-blue-950/40" />
      <div className="absolute top-1/4 right-1/4 h-96 w-96 rounded-full bg-blue-500/8 blur-[120px] dark:bg-blue-600/10" />
      <div className="absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-emerald-500/8 blur-[120px] dark:bg-emerald-600/10" />

      <div className="absolute inset-0 opacity-40 dark:opacity-50">
        <ParticleNetwork />
      </div>

      <div className="relative container mx-auto px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center rounded-full border border-slate-200/70 bg-white/70 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur-xl dark:border-slate-800/70 dark:bg-slate-900/70 dark:text-slate-300"
          >
            {t("hero_badge")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="mb-6 text-5xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-6xl lg:text-7xl"
          >
            <span className="bg-gradient-to-r from-slate-950 via-slate-700 to-blue-700 bg-clip-text text-transparent dark:from-white dark:via-slate-200 dark:to-blue-400">
              {t("hero_title")}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="mx-auto mb-8 max-w-3xl text-xl font-semibold tracking-tight text-slate-800 dark:text-slate-100 sm:text-2xl"
          >
            {t("hero_subtitle")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="mx-auto mb-10 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-400"
          >
            {t("hero_description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.32 }}
            className="mb-14 flex flex-col justify-center gap-4 sm:flex-row"
          >
            <Button
              size="lg"
              className="rounded-full bg-blue-600 px-6 text-white shadow-[0_16px_45px_-20px_rgba(37,99,235,0.7)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_20px_55px_-18px_rgba(37,99,235,0.8)]"
              onClick={handleDeployAIClick}
            >
              {t("deploy_ai")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="rounded-full border border-slate-200 bg-white/80 text-slate-800 shadow-sm transition-all duration-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-900"
              onClick={handleExploreAutomationClick}
            >
              {t("explore_automation")}
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.36 }}
            className="mx-auto mb-12 max-w-5xl"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              {t("hero_segments_intro")}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {targetSegments.map((segment) => (
                <span
                  key={segment}
                  className="rounded-full border border-slate-200/80 bg-white/75 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:text-slate-200"
                >
                  {segment}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3"
          >
            {features.map((feature, index) => (
              <div
                key={index}
                className="soft-card group text-left"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100/90 dark:bg-slate-900/90">
                  {feature.icon}
                </div>
                <h3 className="mb-2 text-xl font-semibold text-slate-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                  {feature.title}
                </h3>
                <p className="text-sm leading-7 text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
